// Compose a campaign: what it says, who sees it, how often. Every audience change re-asks the
// server for the reach, so the number under the form is the number that will be targeted.
import React, { useEffect, useState } from 'react';
import { fetchGeo, fetchReach, searchMembers } from '../../api/popups';
import { DEFAULT_CTA, FORMATS, GENDERS, PLACEMENTS, audienceLabel, composeError, num } from './format';
import ImageField from './ImageField';
import CloseButton from './CloseButton';

const Compose = ({ draft, patch, editing, onLaunch, onSave, onClear, launching }) => {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityMode, setCityMode] = useState('all');
  const [reach, setReach] = useState(null);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const { format, placement, image, title, body, mode, gender, country, city, members } = draft;

  useEffect(() => {
    fetchGeo().then((rows) => setCountries(rows || [])).catch(() => setCountries([]));
  }, []);

  // Cities are per country, so "All countries" has none to narrow to.
  useEffect(() => {
    if (!country || country === 'all') {
      setCities([]);
      return;
    }
    fetchGeo(country).then((rows) => setCities(rows || [])).catch(() => setCities([]));
  }, [country]);

  // Debounced: typing a city name would otherwise fire a count per keystroke. A hand-picked
  // audience needs no estimate — it is exactly as big as the list.
  useEffect(() => {
    if (mode === 'members') return undefined;
    const id = setTimeout(() => {
      fetchReach({ mode, gender, country, city }).then(setReach).catch(() => setReach(null));
    }, 300);
    return () => clearTimeout(id);
  }, [mode, gender, country, city]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      return undefined;
    }
    const id = setTimeout(() => {
      searchMembers(query.trim()).then((rows) => setSuggestions(rows || [])).catch(() => setSuggestions([]));
    }, 300);
    return () => clearTimeout(id);
  }, [query]);

  const pickCountry = (value) => {
    // "All countries" cannot narrow to one city — snap the city selection back with it.
    setCityMode('all');
    patch({ country: value, city: '' });
  };

  const addMember = (member) => {
    if (!members.some((m) => m._id === member._id)) patch({ members: [...members, member] });
    setQuery('');
    setSuggestions([]);
  };

  // An edit never re-targets, so the audience half of the validation does not apply to one.
  const problem = composeError(editing ? { ...draft, mode: 'segment' } : draft);
  const reachLabel = audienceLabel(draft);
  const selected = mode === 'members' ? members.length : reach;

  return (
    <div className="card">
      <h3>{editing ? 'Edit campaign' : 'Compose'}</h3>
      {editing && (
        <p className="tmpl-intro">
          Editing a campaign already running. Its copy, design, placement and display rules can all
          change; <b>who sees it cannot</b> — the audience was counted at launch and the reach
          percentage is measured against that count. To target someone else, end this one and launch
          another.
        </p>
      )}

      <span className="lab">Format</span>
      <div className="chips">
        {FORMATS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`chip ${format === f.id ? 'on' : ''}`}
            onClick={() => patch({ format: f.id })}
          >
            {f.label}
          </button>
        ))}
      </div>

      <span className="lab">Placement</span>
      <div className="chips">
        {PLACEMENTS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`chip ${placement === p.id ? 'on' : ''}`}
            onClick={() => patch({ placement: p.id })}
          >
            {p.label}
          </button>
        ))}
      </div>

      {format !== 'text' && (
        <>
          <span className="lab">Image</span>
          <ImageField
            value={image}
            onChange={(url) => patch({ image: url })}
            hint={
              image
                ? 'Cropped to 16:10 — drag it in the preview to choose what stays in frame.'
                : 'Full-width on the pop-up — festival art, offer cards, launch banners.'
            }
          />
        </>
      )}

      {format !== 'img' && (
        <>
          <label className="lab" htmlFor="pn-title">Headline</label>
          <input
            id="pn-title"
            className="txt"
            maxLength={60}
            value={title}
            onChange={(e) => patch({ title: e.target.value })}
            placeholder="e.g. Diwali on Favor — 40% off Gold"
          />

          <label className="lab" htmlFor="pn-body">Content</label>
          <textarea
            id="pn-body"
            className="txt"
            maxLength={180}
            value={body}
            onChange={(e) => patch({ body: e.target.value })}
            placeholder="One or two lines — the pop-up should be read in three seconds."
          />
          <div className="count">{body.length} / 180</div>
        </>
      )}

      <label className="lab" htmlFor="pn-cta">Button text · opens the plans screen</label>
      <input
        id="pn-cta"
        className="txt"
        maxLength={30}
        value={draft.cta}
        onChange={(e) => patch({ cta: e.target.value })}
        placeholder={DEFAULT_CTA}
      />

      <hr className="sep" />
      {editing ? (
        <>
          <span className="lab">Audience</span>
          <div className="reach">
            <b>{num(editing.audienceSize)}</b>
            <span>
              members were targeted at launch — <b>{audienceLabel(editing.audience || {})}</b>.
              <br />Fixed for the life of this campaign.
            </span>
          </div>
        </>
      ) : (
      <>
      <span className="lab">Send to</span>
      <div className="chips">
        <button
          type="button"
          className={`chip ${mode === 'segment' ? 'on' : ''}`}
          onClick={() => patch({ mode: 'segment' })}
        >
          Audience segment
        </button>
        <button
          type="button"
          className={`chip ${mode === 'members' ? 'on' : ''}`}
          onClick={() => patch({ mode: 'members' })}
        >
          Specific members
        </button>
      </div>

      {mode === 'segment' ? (
        <>
          <span className="lab">Audience · gender</span>
          <div className="chips">
            {GENDERS.map((g) => (
              <button
                key={g.id}
                type="button"
                className={`chip ${gender === g.id ? 'on' : ''}`}
                onClick={() => patch({ gender: g.id })}
              >
                {g.label}
              </button>
            ))}
          </div>

          <span className="lab">Audience · location</span>
          <select
            className="geo-sel"
            style={{ marginBottom: 10 }}
            value={country}
            onChange={(e) => pickCountry(e.target.value)}
          >
            <option value="all">All countries</option>
            {countries.map((c) => (
              <option key={c.name} value={c.name}>{`${c.name} · ${num(c.count)}`}</option>
            ))}
          </select>

          <div className="chips" style={{ marginBottom: 10 }}>
            <button
              type="button"
              className={`chip ${cityMode === 'all' ? 'on' : ''}`}
              onClick={() => { setCityMode('all'); patch({ city: '' }); }}
            >
              All cities
            </button>
            <button
              type="button"
              className={`chip ${cityMode === 'city' ? 'on' : ''}`}
              disabled={country === 'all'}
              onClick={() => setCityMode('city')}
            >
              Specific cities
            </button>
          </div>

          {cityMode === 'city' && (
            <>
              <input
                className="txt"
                value={city}
                onChange={(e) => patch({ city: e.target.value })}
                placeholder="Type a city, or pick one below"
              />
              <div className="chips" style={{ marginTop: 8 }}>
                {cities.slice(0, 8).map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    className="city-sug"
                    onClick={() => patch({ city: c.name })}
                  >
                    {`${c.name} · ${num(c.count)}`}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <span className="lab">Members</span>
          <div className="ppl-box">
            {members.map((m) => (
              <span key={m._id} className="ppl-chip">
                {m.firstName || m.email}
                <CloseButton
                  label={`Remove ${m.firstName || m.email}`}
                  onClick={() => patch({ members: members.filter((x) => x._id !== m._id) })}
                />
              </span>
            ))}
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name or email — add one or many…"
            />
          </div>
          {suggestions.length > 0 && (
            <div className="ppl-sugs">
              {suggestions.map((m) => (
                <button key={m._id} type="button" className="ppl-sug" onClick={() => addMember(m)}>
                  <span><b>{m.firstName || '—'}</b> · {m.email}</span>
                  <i>{[m.gender, m.city].filter(Boolean).join(' · ') || 'no location'}</i>
                </button>
              ))}
            </div>
          )}
          <div className="img-hint" style={{ marginTop: 7 }}>
            The pop-up shows only to the members you add here.
          </div>
        </>
      )}

      <div className="reach">
        <b>{selected === null ? '—' : num(selected)}</b>
        <span>
          {mode === 'members' ? (
            <>member{members.length === 1 ? '' : 's'} selected — <b>shown only to them</b>.<br />Search above to add more.</>
          ) : (
            <>members will see this — <b>{reachLabel}</b>.<br />Estimate updates as you change the audience.</>
          )}
        </span>
      </div>
      </>
      )}

      <hr className="sep" />
      <span className="lab">Display rules</span>
      <div className="rules">
        <label htmlFor="pn-perday">
          Shows per member per day
          <select
            id="pn-perday"
            value={draft.perDay}
            onChange={(e) => patch({ perDay: Number(e.target.value) })}
          >
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
          </select>
        </label>
        <label htmlFor="pn-secs">
          Seconds on screen
          <select
            id="pn-secs"
            value={draft.seconds}
            onChange={(e) => patch({ seconds: Number(e.target.value) })}
          >
            <option value={5}>5</option>
            <option value={8}>8</option>
            <option value={12}>12</option>
            <option value={0}>Until closed</option>
          </select>
        </label>
        <label htmlFor="pn-ends">
          Campaign ends
          <input
            id="pn-ends"
            type="date"
            value={draft.endsAt}
            onChange={(e) => patch({ endsAt: e.target.value })}
          />
        </label>
      </div>

      <div className="actions">
        <button
          type="button"
          className="cta"
          disabled={launching || !!problem}
          title={problem || ''}
          onClick={() => (editing ? onSave() : onLaunch(reach))}
        >
          {launching
            ? editing ? 'Saving…' : 'Launching…'
            : editing ? 'Save changes' : 'Launch pop-up'}
        </button>
        <button type="button" className="cta ghost" onClick={onClear}>
          {editing ? 'Cancel' : 'Clear'}
        </button>
      </div>
      {problem && <div className="img-hint" style={{ marginTop: 10 }}>{problem}</div>}
    </div>
  );
};

export default Compose;
